import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Flex,
  H3,
  P,
  Button,
  Divider,
  Icon,
  Notification,
} from "orcs-design-system";
import PropTypes from "prop-types";
import { useDebounce } from "use-lodash-debounce";

// To resolve issues with jest and react-responsive-masonry, we need to import the Masonry component directly from the lib folder directly
import Masonry from "react-responsive-masonry/lib/Masonry";
import { sanitiseString } from "src/util/search";
import InfiniteScroll from "src/components/InfiniteScroll";
import useUploadHandlers from "./hooks/useUploadHandlers";
import MasonryCard from "./sub-components/MasonryCard";
import { DEFAULT_CARD_WIDTH } from "./consts";
import SearchInput from "./sub-components/SearchInput";
import useImageSelection from "./hooks/useImageSelection";

const FILE_INPUT_ID = "file-input";
const MASONRY_CONTAINER_ID = "masonry-container";
const UPLOADING_IMAGES_NOTIFICATION_ID = "uploading-images-notification";

const GenericMasonry = ({
  fetchItems,
  onUpload,
  limit = 20,
  cardWidth = DEFAULT_CARD_WIDTH,
  title,
  searchable = false,
  searchLabel,
  searchPlaceholder,
  searchErrorText,
  isMultiSelect = false,
  onSelectionChange,
  onDeleteImage,
  scrollContainer = "parent",
  leadingItems = [],
  onClickLeadingItems,
  searchTermRef = null,
  avatarMode = false,
}) => {
  const [page, setPage] = useState(0);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [items, setItems] = useState([]);
  const [containerWidth, setContainerWidth] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchTermRef?.current || "");
  const [searchError, setSearchError] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [deletingImageIds, setDeletingImageIds] = useState([]);

  const [selectedLeadingItems, setSelectedLeadingItems] = useState([]);

  const { onImageClick, selectedImages, setSelectedImages } = useImageSelection(
    {
      images: items,
      isMultiSelect,
      onSelectionChange,
    }
  );

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    if (searchTermRef) {
      // eslint-disable-next-line no-param-reassign
      searchTermRef.current = searchTerm;
    }
  }, [searchTerm, searchTermRef]);

  const {
    handleFileChange,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    // TODO: work with UX to come up with a good patten
    // isDragging,
    uploadingImages,
  } = useUploadHandlers({ onUpload, setItems });

  const fetchPaginatedItems = useCallback(async () => {
    if (searchable && searchTerm.length < 2) {
      return;
    }

    setFetchingMore(true);
    const nextItems = await fetchItems(page, limit, sanitiseString(searchTerm));

    if (nextItems.data) {
      setItems((prevItems) => {
        const merged = [...(prevItems || []), ...(nextItems?.data || [])];
        return Array.from(
          new Map(merged.map((item) => [item.id, item])).values()
        );
      });
      setPage(page + 1);
      setHasMore(nextItems.hasMore);
    }

    if (nextItems.error) {
      setSearchError(true);
    }

    setFetchingMore(false);
  }, [page, limit, fetchItems, searchTerm, searchable]);

  useEffect(() => {
    if (!initialLoadComplete) {
      fetchPaginatedItems();
      setInitialLoadComplete(true);
    }
  }, [initialLoadComplete, fetchPaginatedItems, searchable]);

  useEffect(() => {
    if (debouncedSearchTerm.length > 2) {
      setItems([]);
      setPage(0);
      setInitialLoadComplete(false);
      setHasMore(true);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setContainerWidth(entry.target.clientWidth);
      });
    });

    const container = document.getElementById(MASONRY_CONTAINER_ID);

    if (container) {
      resizeObserver.observe(container);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const triggerUpload = useCallback(() => {
    const fileInput = document.getElementById(FILE_INPUT_ID);

    fileInput.click();
  }, []);

  const columnsCount = useMemo(() => {
    const calculated = Math.floor(containerWidth / cardWidth);
    return calculated > 0 ? calculated : 1;
  }, [containerWidth, cardWidth]);

  const onDeleteItem = useCallback(
    async (item) => {
      setDeletingImageIds((prevIds) => [...prevIds, item.id]);
      await onDeleteImage(item.id);
      setDeletingImageIds((prevIds) => prevIds.filter((id) => id !== item.id));
      setItems((prevItems) =>
        prevItems.filter((prevItem) => prevItem.id !== item.id)
      );
    },
    [onDeleteImage]
  );

  useEffect(() => {
    if (uploadingImages.length > 0) {
      document
        .getElementById(UPLOADING_IMAGES_NOTIFICATION_ID)
        ?.scrollIntoView({ behavior: "smooth" });
      document
        .getElementById(MASONRY_CONTAINER_ID)
        ?.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [uploadingImages.length]);

  return (
    <Flex flexDirection="column" gap="r" height="100%">
      {searchable && (
        <SearchInput
          onSearch={setSearchTerm}
          searchTerm={searchTerm}
          searchError={searchError}
          searchLabel={searchLabel}
          searchPlaceholder={searchPlaceholder}
          searchErrorText={searchErrorText}
        />
      )}
      <Flex>
        {onUpload && (
          <Button iconLeft onClick={triggerUpload}>
            <Icon icon={["fas", "cloud-upload"]} />
            Upload
          </Button>
        )}
      </Flex>
      {title && <H3>{title}</H3>}
      {(title || searchable) && <Divider />}

      {uploadingImages.length > 0 && (
        <Notification
          id={UPLOADING_IMAGES_NOTIFICATION_ID}
          altStyle
          closable={false}
          loading
        >
          Uploading {uploadingImages.length}{" "}
          {uploadingImages.length === 1 ? "image" : "images"}. Please do not
          close or navigate away from the page.
        </Notification>
      )}
      <Box
        flex="1"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        overflowY="auto"
        id={MASONRY_CONTAINER_ID}
        data-testid={MASONRY_CONTAINER_ID}
        // Prevents the hover effects from being cut off from overflow container
        padding="1px"
      >
        {onUpload && (
          <input
            style={{ display: "none" }}
            id={FILE_INPUT_ID}
            type="file"
            multiple
            onChange={handleFileChange}
          />
        )}

        {initialLoadComplete &&
          (items.length > 0 || leadingItems.length > 0) &&
          columnsCount > 0 && (
            <InfiniteScroll
              scrollContainer={scrollContainer}
              hasMore={hasMore}
              loadMore={fetchPaginatedItems}
              threshold={500}
            >
              <Masonry columnsCount={columnsCount} gutter={12} sequential>
                {uploadingImages.map((item, index) => (
                  <MasonryCard
                    key={item.id}
                    data={item}
                    index={index}
                    isUploading
                    onClick={
                      onSelectionChange ? (e) => onImageClick(e, item.id) : null
                    }
                    avatarMode={avatarMode}
                  />
                ))}

                {leadingItems?.map((item, index) => (
                  <MasonryCard
                    key={item.id}
                    data={item}
                    index={index}
                    isDummy={item.isDummy}
                    isSelected={selectedLeadingItems.includes(item.id)}
                    onClick={() => {
                      if (onClickLeadingItems) {
                        setSelectedImages([]);
                        setSelectedLeadingItems((prev) =>
                          prev.includes(item.id) ? [] : [item.id]
                        );
                        onClickLeadingItems(item);
                      }
                    }}
                    avatarMode={avatarMode}
                  />
                ))}

                {items.map((item, index) => (
                  <MasonryCard
                    key={item.id}
                    data={item}
                    index={index}
                    isDeleting={deletingImageIds.includes(item.id)}
                    isSelected={selectedImages.includes(item.id)}
                    onClick={
                      onSelectionChange
                        ? (e) => {
                            onImageClick(e, item.id);
                            if (selectedLeadingItems.length > 0) {
                              setSelectedLeadingItems([]);
                            }
                          }
                        : null
                    }
                    onDelete={onDeleteImage ? () => onDeleteItem(item) : null}
                    avatarMode={avatarMode}
                  />
                ))}

                {fetchingMore &&
                  Array.from({ length: limit }).map((_, index) => (
                    <MasonryCard
                      key={`loading-${_}`}
                      data={{
                        id: `loading-${_}`,
                        height: 3888,
                        width: 5184,
                      }}
                      index={index}
                      isLoading
                      avatarMode={avatarMode}
                    />
                  ))}
              </Masonry>
            </InfiniteScroll>
          )}
        {items.length === 0 && !!onUpload && (
          <Flex
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            gap="r"
            height="100%"
            py="xxl"
          >
            <Flex flexDirection="column" gap="r" alignItems="center" py="xxl">
              <H3>Drop files here to upload</H3>
              <P>or click the Upload button</P>
            </Flex>
          </Flex>
        )}
        {searchable && items.length === 0 && leadingItems.length === 0 && (
          <Flex justifyContent="center" alignItems="center" height="100%">
            <H3>
              {searchTerm.length > 2 ? "Searching..." : "No images found"}
            </H3>
          </Flex>
        )}
      </Box>
    </Flex>
  );
};

GenericMasonry.propTypes = {
  fetchItems: PropTypes.func.isRequired,
  onUpload: PropTypes.func,
  limit: PropTypes.number,
  cardWidth: PropTypes.number,
  title: PropTypes.string,
  searchable: PropTypes.bool,
  searchLabel: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  searchErrorText: PropTypes.string,
  isMultiSelect: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  onDeleteImage: PropTypes.func,
  scrollContainer: PropTypes.string,
  leadingItems: PropTypes.arrayOf(PropTypes.object),
  onClickLeadingItems: PropTypes.func,
  searchTermRef: PropTypes.object,
  avatarMode: PropTypes.bool,
};

export default GenericMasonry;
